import { useState } from 'react';

import { Button, Modal, ModalActions, ModalContent, ModalWrapper } from '@braskai/ui-kit';
import { useForm } from 'react-hook-form';

import { Stack } from '@mui/material';

import { useCreateAccount, useCreateUser } from '@/entities/user/currentUser';

import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { validateEmail } from '@/shared/lib/validators';
import { useRewardfulStore } from '@/shared/thirdParty/rewardful';
import { ControlledTextField } from '@/shared/ui/form';
import { ModalHeaderDefault } from '@/shared/ui/modal';

import { useAddEmailModalStore } from '../model/useAddEmailModalStore';

type FormValues = {
  confirmEmail: string;
  email: string;
};

const AddEmailModalComponent = () => {
  const referral = useRewardfulStore((state) => state.referralId);
  const { mutateAsync: createUser } = useCreateUser({
    isSocial: true,
  });
  const { mutateAsync: createAccount } = useCreateAccount();
  const onClose = useAddEmailModalStore((state) => state.close);

  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    formState: { isValid },
    watch,
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      confirmEmail: '',
      email: '',
    },
  });

  const email = watch('email');

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);

    try {
      await createUser({
        email: values.email,
        referral_id: referral || '',
      });
      await createAccount({
        email: values.email,
        referral: referral || '',
      });
      onClose();
    } catch (e) {
      showErrorAlert(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal size="xs" open>
      <form onClick={handleSubmit(onSubmit)}>
        <ModalHeaderDefault
          title="Confirm your email address"
          subtitle="Double-checking ensures you can always access your account"
        />
        <ModalContent noPadding>
          <Stack gap={12}>
            <ControlledTextField
              label="Email"
              control={control}
              fieldName="email"
              placeholder="Your email"
              type="email"
              validator={validateEmail}
              variant="outlined"
            />
            <ControlledTextField
              label="Repeat email"
              validator={(value: string) => {
                if (value.toLowerCase() !== email.toLowerCase()) {
                  return "Email addresses don't match";
                }

                return validateEmail(value);
              }}
              control={control}
              fieldName="confirmEmail"
              placeholder="Your email"
              type="email"
              variant="outlined"
            />
          </Stack>
        </ModalContent>
        <ModalActions>
          <Button disabled={!isValid} isLoading={isLoading} size="xl" type="submit" fullWidth>
            Next question
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
};

export const AddEmailModal = () => (
  <ModalWrapper hook={useAddEmailModalStore}>
    <AddEmailModalComponent />
  </ModalWrapper>
);
